import React from "react";
import ReactDOM from "react-dom/client";
import CfcCycle from "./CfcCycle";
import _ from "lodash";
import CfcRegistrations from "./CfcRegistrations";
import DateFormatter from "../../../app/formatters/DateFormatter";
import { DndContext } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import * as datefns from "date-fns";

/** @typedef { import('../models/CfcRegistrationModel').CfcRegistrationItem } CfcRegistrationItem */
/** @typedef { import('../models/CfcCycleModel').CfcCycleItem } CfcCycleItem */
/** @typedef { import('../models/CfcWodModel').CfcWodItem } CfcWodItem */
/** @typedef { import('../models/CfcCycleUserModel').CfcCycleUserItem } CfcCycleUserItem */
/** @typedef { import('../models/CfcCategoryModel').CfcCategoryItem } CfcCategoryItem */
/** @typedef { import('../services/CfcCycleService').CfcCyclesApiObj } CfcCyclesApiObj */
/** @typedef { import('../controllers/CfcCycleController').CfcCycleAddRandomApiObj } CfcCycleAddRandomApiObj */

/**
 * @typedef {Object} AdditionnalPropertiesCfcCycleItem
 * @property {string} [custom_id] - Identifiant personnalisé pour le drag and drop
 * @typedef {import('../models/CfcCycleModel').CfcCycleItem & AdditionnalPropertiesCfcCycleItem} ModifiedCfcCycleItem
 */

/**
 * @typedef {Object} AdditionnalPropertiesCfcCycleUserItem
 * @property {string} [custom_id] - Identifiant personnalisé pour le drag and drop
 * @typedef {import('../models/CfcCycleUserModel').CfcCycleUserItem & AdditionnalPropertiesCfcCycleUserItem} ModifiedCfcCycleUserItem
 */

/**
 * @typedef {object} CfcCycleIndexProps
 * @property {boolean} isLoading - Indique si le composant est en cours de chargement
 * @property {number} id - Identifiant de la compétition
 * @property {Array<CfcCategoryItem>} categories - Liste des catégories
 * @property {?number} cfcTournamentId - Identifiant de la compétition
 * @property {Array<ModifiedCfcCycleItem>} cycles - Liste des vagues
 * @property {Array<CfcRegistrationItem>} registrations - Liste des inscriptions
 * @property {Array<CfcRegistrationItem>} registrationsToDrag - Liste des inscriptions à déplacer
 * @property {Array<CfcWodItem>} wods - Liste des wods
 * @property {string} hourStr - Chaîne de caractères représentant l'heure
 * @property {CfcCategoryItem} currentCategory - Catégorie en cours
 * @property {CfcWodItem} currentWod - Wod en cours
 * @property {Array<ModifiedCfcCycleItem>} currentCycles - Liste des vagues en cours
 * @property {Array<ModifiedCfcCycleUserItem>} currentCyclesUsers - Liste des utilisateurs des vagues en cours
 */

class CfcCycleIndex extends React.Component {
  /**
   * @param {CfcCycleIndexProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      id: props.id,
      categories: /** @type {Array<CfcCategoryItem>}*/ ([]),
      cfcTournamentId: null,
      cycles: /** @type {Array<ModifiedCfcCycleItem>}*/ ([]),
      registrations: [],
      registrationsToDrag: /** @type {Array<CfcRegistrationItem>}*/ ([]),
      wods: /** @type {Array<CfcWodItem>} */ ([]),
      hourStr: "",
      currentCategory: /** @type {any}*/ ({
        cfc_wods: []
      }),
      currentWod: /** @type {CfcWodItem}*/ ({
        display_cycles: false,
        number_per_cycle: /** @type {any} */ (""),
        start_date: "",
        duration: "",
        transition: ""
      }),
      currentCycles: /** @type {Array<ModifiedCfcCycleItem>}*/ ([]),
      currentCyclesUsers: /** @type {Array<CfcCycleUserItem>}*/ ([])
    };
  }

  async componentDidMount() {
    const cfcTournamentId = /** @type {number}*/ (this.state.id);

    this.setState(
      {
        cfcTournamentId
      },
      async () => {
        const resultCfcCyclesObj = await this.fetchCfcCyclesObj(cfcTournamentId);

        const currentCategory = resultCfcCyclesObj.categories[0];
        const wodsOfCategory = resultCfcCyclesObj.wods.filter(cfcWod => cfcWod.cfc_category.id === currentCategory.id);
        const currentWod = wodsOfCategory[0];

        resultCfcCyclesObj.cycles = this.formatCfcCycles(resultCfcCyclesObj.cycles);

        const currentCycles = this.getCurrentCycles(resultCfcCyclesObj.cycles, currentCategory, currentWod);
        const registrationsToDrag = this.getRegistrationsToDrag(resultCfcCyclesObj.registrations, currentCategory, currentCycles);

        this.setState({
          categories: resultCfcCyclesObj.categories,
          cycles: resultCfcCyclesObj.cycles,
          registrations: resultCfcCyclesObj.registrations,
          registrationsToDrag,
          wods: resultCfcCyclesObj.wods,
          currentCategory,
          currentWod,
          currentCycles
        });
      }
    );
  }

  /**
   * Permet de récupérer l'objet des vagues de la compétition
   * @param {number} cfcTournamentId - Identifiant de la compétition
   * @returns {Promise<CfcCyclesApiObj>} - Liste des vagues
   */
  async fetchCfcCyclesObj(cfcTournamentId) {
    const apiUrl = `/cfc-cycle/__api/index/${cfcTournamentId}`;
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de générer aléatoirement les vagues et d'y ajouter les athlètes
   * @returns {Promise<CfcCycleAddRandomApiObj>} - Liste des vagues
   */
  async fetchAddCfcCyclesRandom() {
    const apiUrl = `/cfc-cycle/__api/add/random/${this.state.cfcTournamentId}`;
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de mettre à jour les vagues de la compétition
   * @param {number} cfcTournamentId - Identifiant de la compétition
   * @param {Array<ModifiedCfcCycleItem>} cfcCycles - Liste des vagues
   * @returns {Promise<CfcCyclesApiObj>} - Liste des vagues
   */
  async fetchUpdateCfcCycles(cfcTournamentId, cfcCycles) {
    const apiUrl = `/cfc-cycle/__api/update`;
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      },
      body: JSON.stringify({
        cfcTournamentId,
        cycles: cfcCycles
      })
    });

    return await response.json();
  }

  /**
   * Permet de mettre à jour le wod de la compétition
   * @param {CfcWodItem} newCfcWod - Objet du wod modifié
   * @returns {Promise<Array<CfcWodItem>>} - Liste des wods
   */
  async fetchUpdateCfcWod(newCfcWod) {
    const apiUrl = `/cfc-wod/__api/update`;
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      },
      body: JSON.stringify({
        cfcWod: newCfcWod
      })
    });

    return await response.json();
  }

  /**
   * Génération RAMDOM de toutes les vagues (tous les wods et toutes les catégories)
   */
  generateCyclesRandom() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        const resultAddCfcCyclesRandom = await this.fetchAddCfcCyclesRandom();

        const currentCategory = resultAddCfcCyclesRandom.categories[0];
        const wodsOfCategory = resultAddCfcCyclesRandom.wods.filter(cfcWod => cfcWod.cfc_category.id === currentCategory.id);
        const currentWod = wodsOfCategory[0];

        resultAddCfcCyclesRandom.cycles = this.formatCfcCycles(resultAddCfcCyclesRandom.cycles);

        const currentCycles = this.getCurrentCycles(resultAddCfcCyclesRandom.cycles, currentCategory, currentWod);
        const registrationsToDrag = this.getRegistrationsToDrag(resultAddCfcCyclesRandom.registrations, currentCategory, currentCycles);

        this.setState({
          categories: resultAddCfcCyclesRandom.categories,
          cycles: resultAddCfcCyclesRandom.cycles,
          registrations: resultAddCfcCyclesRandom.registrations,
          wods: resultAddCfcCyclesRandom.wods,
          registrationsToDrag,
          currentCategory,
          currentWod,
          currentCycles,
          isLoading: false
        });
      }
    );
  }

  /* --------------- EVENT FUNCTION --------------- */
  /**
   * Enregistrement des vagues
   */
  saveCycles() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        const resultUpdateCfcCycles = await this.fetchUpdateCfcCycles(/** @type {any} */ (this.state.cfcTournamentId), this.state.cycles);
        const currentCategory = resultUpdateCfcCycles.categories[0];
        const wodsOfCategory = resultUpdateCfcCycles.wods.filter(cfcWod => cfcWod.cfc_category.id === currentCategory.id);
        const currentWod = wodsOfCategory[0];

        resultUpdateCfcCycles.cycles = this.formatCfcCycles(resultUpdateCfcCycles.cycles);

        this.setState({
          categories: resultUpdateCfcCycles.categories,
          cycles: resultUpdateCfcCycles.cycles,
          registrations: resultUpdateCfcCycles.registrations,
          wods: resultUpdateCfcCycles.wods,
          currentCategory,
          currentWod,
          currentCycles: this.getCurrentCycles(resultUpdateCfcCycles.cycles, currentCategory, currentWod),
          isLoading: false
        });
      }
    );
  }

  /**
   * Mise à jour des informations du wod courant
   * @param {'display_cycles'|'number_per_cycle'|'duration'|'transition'|'start_date'} property - Propriété du wod à changer
   * @param {PointerEvent} e - Événement associé au changement de valeur dans les champs d'édition du wod courant
   */
  async saveWod(property, e) {
    var updater = /** @type {CfcWodItem} */ ({}),
      target = /** @type {HTMLInputElement}*/ (e.target),
      startDate,
      hourStr;

    switch (property) {
      case "display_cycles":
        updater.display_cycles = target.value === "true";
        updater.id = this.state.currentWod.id;
        break;
      case "number_per_cycle":
        updater.number_per_cycle = parseInt(target.value, 10);
        updater.id = this.state.currentWod.id;
        break;
      case "duration":
        updater.duration = target.value;
        updater.id = this.state.currentWod.id;
        break;
      case "transition":
        updater.transition = target.value;
        updater.id = this.state.currentWod.id;
        break;
      case "start_date":
        hourStr = target.value;
        if (hourStr) {
          startDate = datefns.parse(this.state.currentWod.start_date, "yyyy-MM-dd HH:mm:ss", new Date());
          startDate = datefns.setHours(startDate, parseInt(hourStr.split(":")[0], 10));
          startDate = datefns.setMinutes(startDate, parseInt(hourStr.split(":")[1], 10));
          updater.start_date = datefns.format(startDate, "yyyy-MM-dd HH:mm:ss");
          updater.id = this.state.currentWod.id;
        }
        break;
    }

    if (Object.keys(updater).length) {
      const resultUpdateCfcWod = await this.fetchUpdateCfcWod(updater);

      this.setState({
        wods: resultUpdateCfcWod,
        currentWod: resultUpdateCfcWod.find(cfcWod => {
          return cfcWod.id === updater.id;
        })
      });
    }
  }

  /**
   * Création d'une vague dans la catégorie et le wod courant, en ajouter les CfcCycleRegistration temporaire avec le custom_id pour pouvoir faire du drag and drop dessus
   */
  addCycle() {
    const cfcWod = this.state.currentWod;
    const newCycle = /** @type {ModifiedCfcCycleItem}*/ ({
      cfcCategoryId: this.state.currentCategory.id,
      cfcTournamentId: /** @type {number}*/ (/** @type {unknown}*/ (this.state.cfcTournamentId)),
      cfcWodId: this.state.currentWod.id,
      nb_per_cycle: this.state.currentWod.number_per_cycle
    });
    const currentCfcCycles = this.state.currentCycles;
    const previousCycle = currentCfcCycles[currentCfcCycles.length - 1];
    const duration = parseInt(cfcWod.duration, 10);
    const transition = parseInt(cfcWod.transition, 10);
    const position = previousCycle ? previousCycle.position + 1 : 1;
    const wodStartDate = datefns.addMinutes(new Date(cfcWod.start_date), (duration + transition) * (position - 1));
    const wodEndDate = datefns.addMinutes(wodStartDate, duration);

    newCycle.start = datefns.format(wodStartDate, "HH:mm");
    newCycle.end = datefns.format(wodEndDate, "HH:mm");
    newCycle.start_date = datefns.format(wodStartDate, "yyyy-MM-dd HH:mm:ss");
    newCycle.end_date = datefns.format(wodEndDate, "yyyy-MM-dd HH:mm:ss");
    newCycle.position = position;

    newCycle.custom_id = `cycle${newCycle.position}_cat${newCycle.cfcCategoryId}_wod${newCycle.cfcWodId}`;
    newCycle.cfc_cycle_users = [...Array(this.state.currentWod.number_per_cycle)].map((cfcCycleRegistration, index) => {
      return this.createVirtualCfcCycleUser(newCycle, index + 1, null);
    });

    this.setState({
      cycles: this.state.cycles.concat(newCycle),
      currentCycles: this.state.currentCycles.concat(newCycle)
    });
  }

  /**
   * Report des athlètes ordonançés sur le wod en cours sur les autres wods avec création des vagues et ajout des athlètes dans le même ordre en respectant le nombre d'athlètes par vague déffini par le wod
   */
  reportSortOnOtherWods() {
    const currentCycles = [...this.state.currentCycles];
    const cycleUsers = currentCycles.flatMap(cycle => {
      return cycle.cfc_cycle_users.filter(cycleUser => {
        return cycleUser.cfc_registration;
      });
    });
    const currentWod = this.state.currentWod;
    const currentNumberPerCycle = currentWod.number_per_cycle;
    const currentCategory = this.state.currentCategory;
    const cfcWodsToGenerate = this.state.wods.filter(cfcWod => {
      return cfcWod.id !== currentWod.id && cfcWod.cfc_category.id === currentCategory.id;
    });
    const cfcTournamentId = /** @type {number}*/ (/** @type {unknown}*/ (this.state.cfcTournamentId));
    const cyclesUpdated = this.state.cycles.filter(cycle => cycle.cfcCategoryId !== currentCategory.id || cycle.cfcWodId === currentWod.id);

    cfcWodsToGenerate.forEach(cfcWod => {
      const numberPerCycle = cfcWod.number_per_cycle;
      let indexCfcCycleUser = 0;

      currentCycles.forEach((currentCfcCycle, indexCurrentCfcCycle) => {
        const newCycle = this.createVirtualCfcCycle(cfcTournamentId, cfcWod, indexCurrentCfcCycle + 1, currentCfcCycle.nb_per_cycle);

        if (currentNumberPerCycle === numberPerCycle) {
          newCycle.cfc_cycle_users.push(
            ...currentCfcCycle.cfc_cycle_users.map(cfcCycleUser => {
              return this.createVirtualCfcCycleUser(newCycle, cfcCycleUser.position, cfcCycleUser.cfc_registration);
            })
          );
        } else {
          [...Array(newCycle.nb_per_cycle)].forEach((cfcCycleRegistration, indexCfcCycleRegistration) => {
            newCycle.cfc_cycle_users.push(this.createVirtualCfcCycleUser(newCycle, indexCfcCycleRegistration + 1, cycleUsers[indexCfcCycleUser]?.cfc_registration));
            indexCfcCycleUser++;
          });
        }
        cyclesUpdated.push(newCycle);
      });
      indexCfcCycleUser = 0;
    });

    this.setState({
      cycles: this.formatCfcCycles(cyclesUpdated)
    });
  }

  /**
   * Mise à jour de la chaîne hourStr du wod pour la mettre dans le libellé
   * @param {PointerEvent} e - Événement associé au changement du champ d'édition de l'heure de début du wod courant
   */
  onChangeStartDateWod(e) {
    var target = /** @type {HTMLInputElement}*/ (e.target);

    this.setState({
      hourStr: target.value
    });
  }

  /**
   * Mise à jour de la vague lors du dragend d'un athlète
   * @param {any} event - Lors du dragend d'un athlète de gauche dans une vague
   */
  onDragEnd(event) {
    const updatedCurrentCfcCycle = this.state.currentCycles;
    let { active, over } = event;

    if (!over?.data.current.accepts.includes(active.id)) {
      return;
    }

    const findedCycle = /** @type {ModifiedCfcCycleItem}*/ (
      updatedCurrentCfcCycle.find(cfcCycle => {
        return over.data.current.cfcCycle.custom_id ? cfcCycle.custom_id === over.data.current.cfcCycle.custom_id : cfcCycle.id === over.data.current.cfcCycle.id;
      })
    );

    const findedCfcRegistration = /** @type {CfcRegistrationItem}*/ (this.state.registrationsToDrag.find(cfcRegistration => cfcRegistration.id === active.id));
    const findedCfcCycleUser = /** @type {ModifiedCfcCycleUserItem}*/ (findedCycle.cfc_cycle_users.find(cfcCycleUser => cfcCycleUser.position === over.data.current.position));

    findedCfcCycleUser.cfcRegistrationId = findedCfcRegistration.id;
    findedCfcCycleUser.cfc_registration = findedCfcRegistration;

    this.setState({
      currentCycles: updatedCurrentCfcCycle,
      registrationsToDrag: this.state.registrationsToDrag.filter(registrationToDrag => {
        return registrationToDrag.id !== active.id;
      })
    });
  }

  /**
   * Supression d'un athlète d'une vague, mise à jour des vagues en cours et des athlètes à drag and drop
   * @param {ModifiedCfcCycleItem} cfcCycle - Item de la vague
   * @param {ModifiedCfcCycleUserItem} cfcCycleUser - Item de l'athlète dans la vague
   */
  onClickOnDelete(cfcCycle, cfcCycleUser) {
    var currentCyclesUpdated = this.state.currentCycles.map(currentCfcCycle => {
      if (currentCfcCycle.custom_id === cfcCycle.custom_id) {
        currentCfcCycle.cfc_cycle_users = /** @type {Array<ModifiedCfcCycleUserItem>}*/ (currentCfcCycle.cfc_cycle_users).map(cfcCycleUserInFor => {
          if (cfcCycleUserInFor.cfcRegistrationId === cfcCycleUser.cfcRegistrationId) {
            cfcCycleUserInFor.cfcRegistrationId = null;
            cfcCycleUserInFor.cfc_registration = null;
            cfcCycleUserInFor.custom_id = `${cfcCycle.custom_id}_registration${cfcCycleUserInFor.position}`;
          }
          return cfcCycleUserInFor;
        });
      }
      return currentCfcCycle;
    });

    this.setState({
      currentCycles: currentCyclesUpdated,
      registrationsToDrag: this.getRegistrationsToDrag(this.state.registrations, this.state.currentCategory, currentCyclesUpdated)
    });
  }

  /**
   * Déplaçement d'un athlète dans une vague vers la gauche, mise à jour des vagues
   * @param {CfcCycleItem} cfcCycle - Item de la vague
   * @param {CfcCycleUserItem} draggingCfcCycleUser - Item de l'athlète dans la vague
   */
  onClickOnMoveLeft(cfcCycle, draggingCfcCycleUser) {
    var droppingCfcCycleUserIndex = cfcCycle.cfc_cycle_users.findIndex(cfcCycleUser => {
      return cfcCycleUser.position === draggingCfcCycleUser.position - 1;
    });

    this.setState({
      currentCycles: this._updatePositionOfCycles(cfcCycle, draggingCfcCycleUser, droppingCfcCycleUserIndex)
    });
  }

  /**
   * Déplaçement d'un athlète dans une vague vers la droite, mise à jour des vagues
   * @param {CfcCycleItem} cfcCycle - Item de la vague
   * @param {CfcCycleUserItem} draggingCfcCycleUser - Item de l'athlète dans la vague
   */
  onClickOnMoveRight(cfcCycle, draggingCfcCycleUser) {
    var droppingCfcCycleUserIndex = cfcCycle.cfc_cycle_users.findIndex(cfcCycleUser => {
      return cfcCycleUser.position === draggingCfcCycleUser.position + 1;
    });

    this.setState({
      currentCycles: this._updatePositionOfCycles(cfcCycle, draggingCfcCycleUser, droppingCfcCycleUserIndex)
    });
  }

  /**
   * Changement de catégorie
   * @param {PointerEvent} e - Événement associé au changement de valeur dans la liste déroulante
   */
  changeCategory(e) {
    var target = /** @type {HTMLInputElement}*/ (e.target),
      selectedId = parseInt(target.value, 10),
      currentCategory = /** @type {CfcCategoryItem}*/ (
        this.state.categories.find(category => {
          return category.id === selectedId;
        })
      ),
      wodsOfCategory = this.state.wods.filter(cfcWod => cfcWod.cfc_category.id === currentCategory.id),
      currentWod = wodsOfCategory[0],
      currentCycles = this.getCurrentCycles(this.state.cycles, currentCategory, wodsOfCategory[0]);

    this.setState({
      currentCategory,
      currentWod,
      registrationsToDrag: this.getRegistrationsToDrag(this.state.registrations, currentCategory, currentCycles),
      currentCycles
    });
  }

  /**
   * Changement de wod
   * @param {PointerEvent} e - Événement associé au changement de valeur dans la liste déroulante
   */
  changeWod(e) {
    var target = /** @type {HTMLInputElement}*/ (e.target),
      selectedId = parseInt(target.value, 10),
      currentWod = /** @type {CfcWodItem}*/ (
        this.state.wods.find(wod => {
          return wod.id === selectedId;
        })
      ),
      currentCycles = this.getCurrentCycles(this.state.cycles, this.state.currentCategory, currentWod);

    this.setState({
      currentWod,
      registrationsToDrag: this.getRegistrationsToDrag(this.state.registrations, this.state.currentCategory, currentCycles),
      currentCycles
    });
  }

  /**
   * Création d'un CfcCycleUser virtuel dans la vague pour pouvoir y déposer via drag and drop un athlète
   * @param {ModifiedCfcCycleItem} cfcCycle - Item de la vague ou on doit rajouter un CfcCycleUser virtuel
   */
  onClickOnAddVirtualCfcCycleUser(cfcCycle) {
    var updatedCfcCycle = this.state.currentCycles.map(currentCfcCycle => {
      if (currentCfcCycle.custom_id === cfcCycle.custom_id) {
        currentCfcCycle.cfc_cycle_users.push(this.createVirtualCfcCycleUser(currentCfcCycle, currentCfcCycle.cfc_cycle_users.length + 1, null));
      }
      return currentCfcCycle;
    });

    this.setState({
      currentCycles: updatedCfcCycle
    });
  }

  /**
   * Supression d'une vague entière avec les athlèets à l'intérieur s'il y en a
   * @param {ModifiedCfcCycleItem} cfcCycle - Item de la vague ou on doit rajouter un CfcCycleUser virtuel
   */
  onClickOnDeleteVirtualCfcCycleUser(cfcCycle) {
    var updatedCurrentCfcCycles = this.state.currentCycles.reduce((newCurrentCfcCycles, currentCfcCycle) => {
        if (currentCfcCycle.custom_id !== cfcCycle.custom_id) {
          newCurrentCfcCycles.push(currentCfcCycle);
        }
        return newCurrentCfcCycles;
      }, /** @type {Array<ModifiedCfcCycleItem>}*/ ([])),
      updatedCfcCycles = this.state.cycles.reduce((newCfcCycles, newCfcCycle) => {
        if (newCfcCycle.custom_id !== cfcCycle.custom_id) {
          newCfcCycles.push(newCfcCycle);
        }
        return newCfcCycles;
      }, /** @type {Array<ModifiedCfcCycleItem>}*/ ([]));

    updatedCurrentCfcCycles = updatedCurrentCfcCycles.map((currentCfcCycle, index) => {
      currentCfcCycle.position = index + 1;
      return currentCfcCycle;
    });

    this.setState({
      currentCycles: updatedCurrentCfcCycles,
      cycles: updatedCfcCycles,
      registrationsToDrag: this.getRegistrationsToDrag(this.state.registrations, this.state.currentCategory, updatedCurrentCfcCycles)
    });
  }

  /* --------------- /EVENT FUNCTION --------------- */

  /* --------------- UTILITY FUNCTION --------------- */
  /**
   * Permet de récupérer les vagues de la catégorie et du wod en cours
   * @param {Array<CfcCycleItem>} allCycles - Liste de toutes les vagues
   * @param {CfcCategoryItem} currentCategory - Catégorie en cours
   * @param {CfcWodItem} currentWod - Wod en cours
   * @returns {Array<CfcCycleItem>}
   */
  getCurrentCycles(allCycles, currentCategory, currentWod) {
    return allCycles.filter(cycle => {
      return cycle.cfcCategoryId === currentCategory.id && cycle.cfcWodId === currentWod.id;
    });
  }

  /**
   * Permet de récupérer la liste des athlètes de la catégorie courante qui ne sont pas encore déplacés dans une vague
   * @param {Array<CfcRegistrationItem>} registrations - Liste de tous les enregistrements athlète de la catégorie courante
   * @param {CfcCategoryItem} currentCategory - Catégorie courante
   * @param {Array<CfcCycleItem>} currentCycles - Liste des vagues en cours
   * @returns {Array<CfcRegistrationItem>}
   */
  getRegistrationsToDrag(registrations, currentCategory, currentCycles) {
    const allRegistrationsInCurrentCategory = registrations.filter(cfcRegistration => {
      return cfcRegistration.cfcCategoryId === currentCategory.id;
    });

    let notInCycleCfcRegistrations;

    if (currentCycles.length) {
      const allCurrentCycleCfcRegistrationIds = _.flattenDeep(
        currentCycles.map(cfcCycle => {
          return cfcCycle.cfc_cycle_users.map(cfcCycleUser => {
            return cfcCycleUser.cfcRegistrationId;
          });
        })
      );
      notInCycleCfcRegistrations = allRegistrationsInCurrentCategory.filter(cfcRegistration => {
        return !allCurrentCycleCfcRegistrationIds.includes(cfcRegistration.id);
      });
    } else {
      notInCycleCfcRegistrations = allRegistrationsInCurrentCategory;
    }

    return notInCycleCfcRegistrations;
  }

  /**
   * Formattage des vagues, pour les enregistrement dans les vagues sans users, on ajoute un CfcCycleUser virtuel pour le drag and drop
   * @param {Array<ModifiedCfcCycleItem>} cfcCycles - Liste des vagues
   * @returns {Array<CfcCycleItem>}
   */
  formatCfcCycles(cfcCycles) {
    return cfcCycles.map(cfcCycle => {
      cfcCycle.custom_id = `cycle${cfcCycle.position}_cat${cfcCycle.cfcCategoryId}_wod${cfcCycle.cfcWodId}`;
      cfcCycle.cfc_cycle_users = /** @type {Array<ModifiedCfcCycleUserItem>}*/ (cfcCycle.cfc_cycle_users).map(cfcCycleUser => {
        if (cfcCycleUser.cfcRegistrationId === null) {
          cfcCycleUser.custom_id = `cycle${cfcCycle.position}_cat${cfcCycle.cfcCategoryId}_wod${cfcCycle.cfcWodId}_registration${cfcCycleUser.position}`;
        }
        return cfcCycleUser;
      });

      if (cfcCycle.cfc_cycle_users.length !== cfcCycle.nb_per_cycle) {
        for (var i = cfcCycle.cfc_cycle_users.length + 1; i <= cfcCycle.nb_per_cycle; i++) {
          cfcCycle.cfc_cycle_users.push(this.createVirtualCfcCycleUser(cfcCycle, i, null));
        }
      }

      return cfcCycle;
    });
  }

  /**
   * Création d'une vague virutel
   * @param {number} cfcTournamentId - Identifiant de la compétition
   * @param {CfcWodItem} cfcWod - Item du wod de la vague à créer
   * @param {number} position - Position de la vague
   * @param {number} nbPerCycle - Nombre d'athlète dans la vague
   * @returns {CfcCycleItem}
   */
  createVirtualCfcCycle(cfcTournamentId, cfcWod, position, nbPerCycle) {
    const duration = parseInt(cfcWod.duration, 10);
    const transition = parseInt(cfcWod.transition, 10);
    const wodStartDate = datefns.addMinutes(new Date(cfcWod.start_date), (duration + transition) * (position - 1));
    const wodEndDate = datefns.addMinutes(wodStartDate, duration);
    const createdCfcCycle = /** @type {ModifiedCfcCycleItem}*/ (
      /** @type {unknown}*/ ({
        cfcCategoryId: cfcWod.cfc_category.id,
        cfcTournamentId,
        cfcWodId: cfcWod.id,
        nb_per_cycle: nbPerCycle,
        start: datefns.format(wodStartDate, "HH:mm"),
        end: datefns.format(wodEndDate, "HH:mm"),
        start_date: datefns.format(wodStartDate, "yyyy-MM-dd HH:mm:ss"),
        end_date: datefns.format(wodEndDate, "yyyy-MM-dd HH:mm:ss"),
        cfc_cycle_users: [],
        position
      })
    );

    createdCfcCycle.custom_id = `cycle${createdCfcCycle.position}_cat${createdCfcCycle.cfcCategoryId}_wod${createdCfcCycle.cfcWodId}`;

    return createdCfcCycle;
  }

  /**
   * Création d'un cfcCycleUser virtuel
   * @param {ModifiedCfcCycleItem} cfcCycle - Item de la vague
   * @param {number} position - Position de l'item cfcCycleRegistration dans la vague
   * @param {?CfcRegistrationItem} cfcRegistration - Item de l'enregistrement athlète
   * @returns {ModifiedCfcCycleUserItem}
   */
  createVirtualCfcCycleUser(cfcCycle, position, cfcRegistration) {
    return /** @type {ModifiedCfcCycleUserItem}*/ ({
      position,
      cfc_registration: cfcRegistration,
      cfcRegistrationId: cfcRegistration?.id || null,
      custom_id: `${cfcCycle.custom_id}_registration${position}`
    });
  }

  /**
   * Mise à jour de la position des athlètes dans les vagues lors du drag and drop
   * @param {ModifiedCfcCycleItem} cfcCycle - Item de la vague
   * @param {CfcCycleUserItem} draggingCfcCycleUser - Item de l'athlète en train d'être déplacé
   * @param {number} droppingCfcCycleUserIndex - Index de l'athlète où on veut déposer l'athlète en train d'être déplacé
   * @returns {Array<CfcCycleItem>} - Liste des vagues mise à jour avec les athlètes déplacés
   * @private
   */
  _updatePositionOfCycles(cfcCycle, draggingCfcCycleUser, droppingCfcCycleUserIndex) {
    var draggingCfcCycleUserIndex = cfcCycle.cfc_cycle_users.findIndex(cfcCycleUser => {
      return cfcCycleUser.cfcRegistrationId === draggingCfcCycleUser.cfcRegistrationId;
    });

    return this.state.currentCycles.map(currentCfcCycle => {
      if (currentCfcCycle.custom_id === cfcCycle.custom_id) {
        currentCfcCycle.cfc_cycle_users = arrayMove(currentCfcCycle.cfc_cycle_users, draggingCfcCycleUserIndex, droppingCfcCycleUserIndex);
        currentCfcCycle.cfc_cycle_users = currentCfcCycle.cfc_cycle_users.map((cfcCycleUser, index) => {
          cfcCycleUser.position = index + 1;
          return cfcCycleUser;
        });
      }
      return currentCfcCycle;
    });
  }
  /* --------------- /UTILITY FUNCTION --------------- */

  /**
   * Complétion des vagues courante avec les CfcCycleUser virtuels
   */
  completeAllCurrentVirtualCfcCycleUser() {
    var updatedCfcCycle = [...this.state.currentCycles],
      cfcRegistrations = this.state.registrationsToDrag,
      indexCfcCycleUser = 0,
      cfcRegistrationToAdd;

    updatedCfcCycle = updatedCfcCycle.map(cfcCycle => {
      cfcCycle.cfc_cycle_users = cfcCycle.cfc_cycle_users.map(cfcCycleUser => {
        cfcRegistrationToAdd = cfcRegistrations[indexCfcCycleUser];

        if (!cfcCycleUser.cfcRegistrationId && cfcRegistrationToAdd) {
          cfcCycleUser.cfcRegistrationId = cfcRegistrationToAdd.id;
          cfcCycleUser.cfc_registration = cfcRegistrationToAdd;
          indexCfcCycleUser++;
        }

        return cfcCycleUser;
      });
      return cfcCycle;
    });

    this.setState({
      currentCycles: updatedCfcCycle,
      registrationsToDrag: this.getRegistrationsToDrag(this.state.registrations, this.state.currentCategory, updatedCfcCycle)
    });
  }

  render() {
    return (
      <div>
        <div className="flexline is-spaced">
          <div>
            <div className="flexline flexline--align-vz flexline--gap-16">
              <button data-tooltip="Générer les vagues aléatoirement" className="btn" onClick={this.generateCyclesRandom.bind(this)}>
                <span className="material-icons material-icons-outlined">shuffle</span>Générer aléatoirement
              </button>
              <a data-tooltip="Généré les vagues par classement" className="btn" href={`/cfc-cycle/__editbyscores/${this.state.cfcTournamentId}`}>
                <span className="material-icons material-icons-outlined">leaderboard</span>Générer par classement
              </a>
              <a data-tooltip="Généré les vagues par classement inversé" className="btn" href={`/cfc-cycle/__editbyscores/${this.state.cfcTournamentId}?reverse=true`}>
                <span className="material-icons material-icons-outlined">leaderboard</span>Générer par classement inversé
              </a>
              <a data-tooltip="Planning de la journée" target="_blank" className="btn" href={`/cfc-cycle/planning/${this.state.cfcTournamentId}`}>
                <span className="material-icons material-icons-outlined">print</span>Planning journée
              </a>
              <a data-tooltip="Supprimer les vagues" className="btn" href={`/cfc-cycle/__delete/${this.state.cfcTournamentId}`}>
                <span className="material-icons">delete</span>Supprimer
              </a>
              <div className="actionlist--divider"></div>
              <button className="btn btn--main-color" onClick={this.saveCycles.bind(this)}>
                <span className="material-icons">save</span>Enregistrer
              </button>
            </div>
          </div>
        </div>

        <div className="space--16"></div>

        {this.state.isLoading ? (
          <div className="loader">
            <img className="loader__image" src="/images/icons/loader.svg" aria-hidden="true" />
          </div>
        ) : (
          ""
        )}
        <DndContext onDragEnd={this.onDragEnd.bind(this)}>
          <div className="grid grid--has-2-child-taken-1-4">
            <div style={{ backgroundColor: "var(--grey-200)", padding: "8px" }}>
              <h3 className="title title--h4">Catégories</h3>
              <div className="space--16"></div>
              <div className="input-group">
                <select className="input-group__input" onChange={this.changeCategory.bind(this)} value={this.state.currentCategory.id}>
                  {this.state.categories.map((category, index) => (
                    <option value={category.id} key={index}>
                      {category.name} - {category.number} inscri(s)
                    </option>
                  ))}
                </select>
              </div>

              {this.state.registrationsToDrag.length ? (
                <div>
                  <div className="flexline flexline--align-hz">
                    <button className="btn btn--transparent-no-label" onClick={this.completeAllCurrentVirtualCfcCycleUser.bind(this)}>
                      <span className="material-icons">control_point_duplicate</span>Ajouter tous les athlètes
                    </button>
                  </div>
                  <div className="space--16"></div>
                </div>
              ) : (
                ""
              )}

              <CfcRegistrations cfcRegistrations={this.state.registrationsToDrag}></CfcRegistrations>
            </div>
            <div>
              <h3 className="title title--h4">Vagues</h3>
              <div className="space--16"></div>
              <div className="grid grid--has-5-child">
                <div className="input-group input-group--no-margin">
                  <label htmlFor="cfcWodId" className="input-group__label">
                    Choix du wod
                  </label>
                  <select className="input-group__input" id="cfcWodId" onChange={this.changeWod.bind(this)} value={this.state.currentWod.id}>
                    {this.state.currentCategory.cfc_wods.map((wod, index) => (
                      <option value={wod.id} key={index}>
                        {wod.name} - {wod.number_per_cycle} athlètes par vague
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group input-group--no-margin">
                  <label htmlFor="displayCycles" className="input-group__label">
                    Afficher les vagues
                  </label>
                  <select className="input-group__input" id="displayCycles" onChange={this.saveWod.bind(this, "display_cycles")} value={this.state.currentWod.display_cycles}>
                    <option value={this.state.currentWod.display_cycles}>{this.state.currentWod.display_cycles ? "Oui" : "Non"}</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>
                <div className="input-group input-group--no-margin">
                  <label htmlFor="start_hour" className="input-group__label">
                    Heure de début : {DateFormatter.getDatepickerHourFormat(this.state.currentWod.start_date)}
                  </label>
                  <input type="text" id="start_hour" className="input-group__input" name="start_date" value={this.state.hourStr} placeholder="Heure de début" onBlur={this.saveWod.bind(this, "start_date")} onChange={this.onChangeStartDateWod.bind(this)} />
                </div>
                <div className="input-group input-group--no-margin">
                  <label htmlFor="duration" className="input-group__label">
                    Durée <small>(en minutes)</small>
                  </label>
                  <input type="text" id="duration" className="input-group__input" placeholder="Durée" value={this.state.currentWod.duration} onChange={this.saveWod.bind(this, "duration")} />
                </div>
                <div className="input-group input-group--no-margin">
                  <label htmlFor="transition" className="input-group__label">
                    Transition <small>(en minutes)</small>
                  </label>
                  <input type="text" id="transition" className="input-group__input" placeholder="Transition" value={this.state.currentWod.transition} onChange={this.saveWod.bind(this, "transition")} />
                </div>
              </div>
              <div>
                <div className="space--16"></div>
                <div className="card card--info">
                  <div className="card__description card__description--no-height">
                    {this.state.registrationsToDrag.length ? (
                      <div className="flexline flexline--align-hz flexline--align-vz flexline--margin">
                        <div className="input-group input-group--no-margin">
                          <select className="input-group__input" onChange={this.saveWod.bind(this, "number_per_cycle")}>
                            <option value={this.state.currentWod.number_per_cycle}>{this.state.currentWod.number_per_cycle} athlètes par vague</option>
                            {[...Array(50)]
                              .map((i, index) => index + 1)
                              .map((i, index) => (
                                <option value={i} key={index}>
                                  {i} athlètes par vague
                                </option>
                              ))}
                          </select>
                        </div>
                        <button className="btn btn--transparent-no-label" onClick={this.addCycle.bind(this)}>
                          <span className="material-icons">add</span>Ajouter une vague
                        </button>
                      </div>
                    ) : (
                      <div className="flexline flexline--align-hz flexline--align-vz flexline--margin">
                        <button className="btn" onClick={this.reportSortOnOtherWods.bind(this)}>
                          <span className="material-icons">control_point_duplicate</span>Générer les autres WOD à partir de ce trie
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {this.state.currentCycles.length ? (
                  <div>
                    <div className="space--16"></div>
                    <div style={{ backgroundColor: "var(--grey-100)", padding: "8px" }}>
                      <div className="mosaic">
                        {this.state.currentCycles.map(cfcCycle => (
                          <CfcCycle key={cfcCycle.custom_id || cfcCycle.id} cfcCycle={cfcCycle} cfcWod={this.state.currentWod} cfcCycleRegistrationIds={this.state.registrationsToDrag.map(r => r.id)} onClickOnDelete={this.onClickOnDelete.bind(this)} onClickOnMoveLeft={this.onClickOnMoveLeft.bind(this)} onClickOnMoveRight={this.onClickOnMoveRight.bind(this)} onClickOnAddVirtualCfcCycleUser={this.onClickOnAddVirtualCfcCycleUser.bind(this)} onClickOnDeleteVirtualCfcCycleUser={this.onClickOnDeleteVirtualCfcCycleUser.bind(this)}></CfcCycle>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </div>
        </DndContext>
      </div>
    );
  }
}

var cfcCycleNode = document.getElementById("cfc-cycle");
if (cfcCycleNode) {
  var root = ReactDOM.createRoot(cfcCycleNode);
  root.render(<CfcCycleIndex id={cfcCycleNode.getAttribute("data-id")} />);
}
